/**
 * useSpeechRecognition — the Web Speech API dictation cluster. Owns
 * `recognitionRef`, `isListeningRef`, the `isListening` / `interimTranscript`
 * state, the one-time recognition init effect, the ref-sync effect, and the
 * start/stop primitives (`toggleVoiceInput`, `stopDictation`). The dictation
 * text helpers live in `components/chatbox/speech-dictation.ts`.
 *
 * Voice-mode (orb) interplay (`cancelVoiceInput`, the voiceMode→dictation sync
 * effect, the mic gesture) stays in ChatBox — those are voice-mode concerns and
 * read the refs/state this hook exposes.
 */
import { useEffect, useRef, useState, type MutableRefObject } from 'react';
import { useToast } from '@/app/providers/ToastContext';
import { normalizeSpeech, trimStartInline, appendDictation } from '../components/chatbox/speech-dictation';

export interface UseSpeechRecognitionDeps {
  setInputValue: React.Dispatch<React.SetStateAction<string>>;
  chatInputRef: MutableRefObject<HTMLTextAreaElement | null>;
  startAudioViz: () => void;
  stopAudioViz: () => void;
}

export interface UseSpeechRecognitionResult {
  isListening: boolean;
  setIsListening: React.Dispatch<React.SetStateAction<boolean>>;
  interimTranscript: string;
  setInterimTranscript: React.Dispatch<React.SetStateAction<string>>;
  isListeningRef: MutableRefObject<boolean>;
  recognitionRef: MutableRefObject<any>;
  /** Toggle dictation on/off (the mic button single-tap path). */
  toggleVoiceInput: () => void;
  /**
   * Stop an in-progress dictation: clears the should-restart ref, stops the
   * recognizer, resets state, and stops the audio visualizer. Idempotent.
   * Used by handleSubmit and the mic gesture's enter-voice-mode branches.
   */
  stopDictation: () => void;
}

export function useSpeechRecognition(deps: UseSpeechRecognitionDeps): UseSpeechRecognitionResult {
  const { showToast } = useToast();
  const { setInputValue, chatInputRef, startAudioViz, stopAudioViz } = deps;

  const [isListening, setIsListening] = useState(false);
  const [interimTranscript, setInterimTranscript] = useState('');
  const recognitionRef = useRef<any>(null);
  const isListeningRef = useRef(false);

  useEffect(() => {
    isListeningRef.current = isListening;
  }, [isListening]);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    // Skip if already initialized (React Strict Mode double-mount protection)
    if (recognitionRef.current) return;

    const SpeechRecognition = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
    if (!SpeechRecognition) return;

    const recognition = new SpeechRecognition();
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.lang = 'en-US';

    recognition.onresult = (event: any) => {
      let interim = '';
      const finals: string[] = [];

      for (let i = event.resultIndex; i < event.results.length; i++) {
        const transcript = normalizeSpeech(event.results[i][0]?.transcript || '');
        if (event.results[i].isFinal) {
          finals.push(transcript);
        } else {
          interim += transcript;
        }
      }

      setInterimTranscript(trimStartInline(interim));

      if (finals.length) {
        setInputValue(prev => {
          let out = prev ?? '';
          for (const piece of finals) {
            out = appendDictation(out, piece);
          }
          return out;
        });
        setInterimTranscript('');
        // Move textarea caret to end so it follows the dictated text.
        requestAnimationFrame(() => {
          const ta = chatInputRef.current;
          if (ta) {
            const end = ta.value.length;
            ta.selectionStart = end;
            ta.selectionEnd = end;
          }
        });
      }
    };

    recognition.onerror = (event: any) => {
      console.warn('[Voice] Speech recognition error:', event.error);
      if (event.error !== 'no-speech' && event.error !== 'aborted') {
        setIsListening(false);
        setInterimTranscript('');
        isListeningRef.current = false;
        stopAudioViz();
      }
    };

    recognition.onend = () => {
      console.log('[Voice] Recognition ended, shouldRestart:', isListeningRef.current);
      if (isListeningRef.current) {
        try {
          recognition.start();
        } catch (err) {
          console.error('[Voice] Failed to restart:', err);
          isListeningRef.current = false;
          setIsListening(false);
        }
      }
    };

    recognitionRef.current = recognition;

    // No cleanup - we want to keep the recognition instance alive
    // React Strict Mode will try to unmount/remount but we ignore it
  }, []);

  const stopDictation = () => {
    isListeningRef.current = false;
    try {
      recognitionRef.current?.stop();
    } catch (_) { /* ignore */ }
    setIsListening(false);
    setInterimTranscript('');
    stopAudioViz();
  };

  const toggleVoiceInput = () => {
    if (!recognitionRef.current) {
      showToast('Voice input is not supported in your browser. Try Chrome or Edge.', 'error');
      return;
    }

    if (isListening) {
      // Set ref immediately to prevent restart in onend
      isListeningRef.current = false;
      try {
        recognitionRef.current.stop();
      } catch (_) {
        // Ignore stop errors
      }
      setIsListening(false);
      setInterimTranscript('');
      stopAudioViz();
    } else {
      // Start speech recognition immediately, audio visualizer in parallel
      isListeningRef.current = true;
      try {
        recognitionRef.current.start();
      } catch (err) {
        console.error('[Voice] Failed to start recognition:', err);
        isListeningRef.current = false;
        return;
      }
      setIsListening(true);
      // Audio visualizer starts in parallel (non-blocking)
      startAudioViz();
    }
  };

  return {
    isListening,
    setIsListening,
    interimTranscript,
    setInterimTranscript,
    isListeningRef,
    recognitionRef,
    toggleVoiceInput,
    stopDictation,
  };
}
